'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { FlixmediaFragmentFragmentDoc } from '@packages/gql/generated/shopping/FlixmediaFragmentFragmentDoc';
import { Card, CardContent, Dialog, Image, Stack, Typography } from '@packages/shared';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useEffect, useState, type FC } from 'react';
import { useIntl } from 'react-intl';
import { addFlixmediaScript, removeFlixmediaScript } from './flixmediaScript';
import { messagesFlixmedia } from './messages';

/* GraphQL */ `
  fragment FlixmediaFragment on Product {
    ean
    brand {
      image
      name
    }
  }
`;

export type FlixmediaProps = {
  productData: FragmentType<typeof FlixmediaFragmentFragmentDoc>;
};

/**
 * Flixmedia component description displayed in storybook
 * */
export const Flixmedia: FC<FlixmediaProps> = ({ productData }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    product: {
      flixmedia: { distributorId },
    },
  } = useConfig();

  const { language } = useI18n();
  const { formatMessage } = useIntl();

  const { ean, brand } = getFragmentData(FlixmediaFragmentFragmentDoc, productData);

  useEffect(() => {
    if (isDialogOpen) {
      addFlixmediaScript(language, distributorId, ean, brand?.name);
    } else {
      removeFlixmediaScript();
    }
  }, [isDialogOpen, language, distributorId, ean, brand]);

  return (
    <div data-testid="flixmedia">
      <Card>
        <CardContent sx={{ cursor: 'pointer' }} onClick={() => setIsDialogOpen(true)}>
          <Stack direction="row" alignItems="center" gap={2.5}>
            {brand?.image && (
              <Image
                loading="lazy"
                src={brand.image}
                alt={brand.name}
                style={{ objectFit: 'contain' }}
                width={50}
                height={30}
              />
            )}
            <Typography variant="h5">
              {formatMessage(messagesFlixmedia.flixmediaCardTitle)}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
      <Dialog
        open={isDialogOpen}
        handleClose={() => setIsDialogOpen(false)}
        size="xl"
        title={formatMessage(messagesFlixmedia.flixmediaModalTitle)}
        dialogContent={<div id="flix-inpage" />}
      />
    </div>
  );
};
